// src/components/media/toast.tsx — a short-lived message at the foot of the screen ("3 files moved", or what failed).
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, AlertTriangle, X } from 'lucide-react';
import { ApiRequestError } from '../../lib/api';

export interface Toast { text: string; tone: 'ok' | 'error' }

export const errorText = (e: unknown) => (e instanceof ApiRequestError || e instanceof Error ? e.message : 'Something went wrong');

export function useToast() {
  const [toast, setToast] = useState<Toast | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.tone === 'error' ? 6000 : 3500);
    return () => clearTimeout(t);
  }, [toast]);
  const ok = useCallback((text: string) => setToast({ text, tone: 'ok' }), []);
  const fail = useCallback((e: unknown) => setToast({ text: errorText(e), tone: 'error' }), []);
  const node = toast && createPortal(
    <div role={toast.tone === 'error' ? 'alert' : 'status'}
      className={`fixed inset-x-4 bottom-4 z-[300] mx-auto flex max-w-md items-start gap-2.5 rounded-lg border px-4 py-3 text-[13px] shadow-pop ${toast.tone === 'error' ? 'border-red-200 bg-red-50 text-red-800' : 'border-ink-200 bg-white text-ink-900'}`}>
      {toast.tone === 'error' ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
      <span className="min-w-0 flex-1">{toast.text}</span>
      <button type="button" aria-label="Dismiss" onClick={() => setToast(null)} className="rounded p-0.5 text-ink-400 hover:text-ink-700"><X className="h-3.5 w-3.5" /></button>
    </div>,
    document.body,
  );
  return { ok, fail, node };
}
